import { defineComponent, onBeforeMount, ref } from 'vue'
import {
  RangeOptions,
  updateType,
  virtualListProps,
  VirtualOptions
} from './props'

import VirtualItem from './virtual-item'

const enum CALC_TYPE {
  INIT = 'INIT',
  FIXED = 'FIXED',
  DYNAMIC = 'DYNAMIC'
}

export default defineComponent({
  name: 'cms-virtual-scroll-list',
  props: virtualListProps,
  components: {
    VirtualItem
  },
  setup(props, {}) {
    const range = ref<RangeOptions | null>(null)

    const update: updateType = newRange => {
      range.value = newRange
    }

    let virtual: ReturnType<typeof initVirtual>
    const getUniqueIdFromDataSources = () => {
      const { dataSources, dataKey } = props
      return dataSources.map(dataSource => {
        return dataSource[dataKey]
      })
    }

    const initVirtual = (param: VirtualOptions, update: updateType) => {
      let offsetValue = 0 //没有滚动前的偏移量
      let fixedSizeVal = 0 // 默认值是0
      let firstRangeAvg = 0
      let caclType = CALC_TYPE.INIT
      const sizes = new Map<string | number, number>()

      const range: RangeOptions = {
        start: 0,
        end: 0,
        padFront: 0,
        padBehind: 0
      }
      function isFixed() {
        return caclType === CALC_TYPE.FIXED
      }
      function getEstimateSize() {
        return isFixed() ? fixedSizeVal : firstRangeAvg || param.estimateSize
      }

      function getIndexOffset(idx: number) {
        if (!idx) return 0
        let offset = 0
        for (let i = 0; i < idx; i++) {
          let indexSize = sizes.get(param.uniqueIds[i])
          offset +=
            typeof indexSize === 'number' ? indexSize : getEstimateSize()
        }
        return offset
      }
      function getPadFront() {
        if (isFixed()) {
          return fixedSizeVal * range.start
        } else {
          return getIndexOffset(range.start)
        }
      }

      function getPadBehind() {
        const lastIndex = param.uniqueIds.length - 1
        return (lastIndex - range.end) * getEstimateSize()
      }

      function updateRange(start: number, end: number) {
        range.start = start
        range.end = end
        range.padFront = getPadFront()
        range.padBehind = getPadBehind()
        update({ ...range })
      }
      function checkRange(start: number, end: number) {
        // 所有的数据
        const total = param.uniqueIds.length
        const keeps = param.keeps
        if (total < keeps) {
          start = 0
          end = total - 1
        } else if (end - start < keeps - 1) {
          start = end - keeps + 1
        }
        updateRange(start, end)
      }

      const getScrollOvers = () => {
        // 根据划过的偏移量 / 每项的高度  就是划过的个数
        if (isFixed()) {
          return Math.floor(offsetValue / getEstimateSize())
        } else {
          let low = 0
          let high = param.uniqueIds.length
          let middle = 0
          let middleOffset = 0
          while (low <= high) {
            middle = low + Math.floor((high - low) / 2)
            middleOffset = getIndexOffset(middle)
            if (middleOffset == offsetValue) {
              return middle
            } else if (middleOffset < offsetValue) {
              low = middle + 1
            } else if (middleOffset > offsetValue) {
              high = middle - 1
            }
          }
          return low > 0 ? --low : 0
        }
      }

      const getEndByStart = (start: number) => {
        const computedEnd = start + param.keeps - 1
        const end = Math.min(computedEnd, param.uniqueIds.length - 1)
        return end
      }
      const handleFront = () => {
        // 获取划过了多少个
        const overs = getScrollOvers()
        if (overs > range.start) {
          return
        }
        const start = Math.max(overs - param.buffer, 0)
        checkRange(start, getEndByStart(start))
      }
      const handleBehind = () => {
        const overs = getScrollOvers()
        if (overs < range.start + param.buffer) {
          return
        }
        checkRange(overs, getEndByStart(overs))
      }
      const handleScroll = (offset: number) => {
        // 先看一下向上滚动还是向下滚动
        const direction = offset < offsetValue ? 'FRONT' : 'BEHIND'
        offsetValue = offset
        if (direction === 'FRONT') {
          handleFront()
        } else if (direction === 'BEHIND') {
          handleBehind()
        }
      }

      const saveSize = (id: number | string, size: number) => {
        sizes.set(id, size)
        // 第一个元素加载完毕后默认是固定高度
        if (caclType === CALC_TYPE.INIT) {
          fixedSizeVal = size
          caclType = CALC_TYPE.FIXED
        } else if (caclType === CALC_TYPE.FIXED && fixedSizeVal !== size) {
          caclType = CALC_TYPE.DYNAMIC
          fixedSizeVal = 0
        }

        if (caclType === CALC_TYPE.DYNAMIC) {
          // 根据已经加载的数据算一个平均值来撑开滚动条
          // 根据当前展示的数据来计算一个滚动条的值
          if (sizes.size < Math.min(param.keeps, param.uniqueIds.length)) {
            firstRangeAvg = Math.round(
              [...sizes.values()].reduce((acc, val) => acc + val, 0) /
                sizes.size
            )
          }
        }
      }
      checkRange(0, param.keeps - 1)
      return { handleScroll, saveSize }
    }

    const installVirtual = () => {
      virtual = initVirtual(
        {
          keeps: props.keeps,
          buffer: Math.round(props.keeps / 3),
          uniqueIds: getUniqueIdFromDataSources(),
          estimateSize: props.estimateSize
        },
        update
      )
    }
    onBeforeMount(() => {
      installVirtual()
    })

    function genRenderComponent() {
      const slots = []
      const { start, end } = range.value!
      const { dataSources, dataComponent, dataKey } = props
      for (let index = start; index <= end; index++) {
        const dataSource = dataSources[index]
        const uniqueKey = (dataSources as any)[dataKey]
        if (dataSource) {
          slots.push(
            <virtual-item
              uniqueKey={uniqueKey}
              source={dataSource}
              component={dataComponent}
              onItemResize={onItemResize}
            />
          )
        }
      }
      return slots
    }

    function onItemResize(id: number | string, size: number) {
      virtual.saveSize(id, size)
    }
    const root = ref<HTMLElement | null>()

    function onscroll() {
      console.log(123, root.value)

      if (root.value) {
        const offset = root.value.scrollTop
        virtual.handleScroll(offset)
      }
    }
    return () => {
      const { padFront, padBehind } = range.value!
      const paddingStyle = {
        padding: `${padFront}px 0 ${padBehind}px`
      }
      return (
        <div onScroll={onscroll} ref={root}>
          <div style={paddingStyle}>{genRenderComponent()}</div>
        </div>
      )
    }
  }
})
